import {
  ChevronFirst,
  ChevronLast,
  ChevronLeftIcon,
  ChevronRightIcon,
  DownloadIcon,
} from 'lucide-react';
import { Button } from '../../Button';
import { Combobox } from '../../Combobox';
import { Label } from '../../Label';
import { Tooltip } from '../../Tooltip';
import { cn } from '../../../lib/utils';
import useI18n from '../../../hooks/useI18n';
import { getPageRange } from '../utils';
import type { TableProps } from '../types';
import { RowData, Table } from '@tanstack/react-table';

type TableFooterProps<TData extends RowData> = {
  table: Table<TData>;
  exportTableData: () => void;
  isFullscreen: boolean;
} & Pick<TableProps<RowData>, 'disableExport' | 'rowsPerPageOptions'>;

export function Footer<TData extends RowData>({
  table,
  exportTableData,
  disableExport,
  rowsPerPageOptions,
  isFullscreen,
}: TableFooterProps<TData>) {
  const { t } = useI18n();

  return (
    <div
      className={cn(
        'eye:my-8 eye:grid eye:grid-cols-3 eye:items-center eye:justify-between eye:gap-y-12',
        isFullscreen && 'eye:px-8 eye:pb-8',
      )}
    >
      {table.getPrePaginationRowModel().rows.length > 1 && (
        <>
          <div>
            <Tooltip content={t('exportAllRows')}>
              <Button
                disabled={disableExport}
                variant='ghost'
                size='icon'
                onClick={() => exportTableData()}
              >
                <DownloadIcon aria-label={t('exportAllRows')} />
              </Button>
            </Tooltip>
          </div>

          <div className='eye:body-s eye:flex eye:justify-center'>
            <div className='eye:hidden eye:md:block'>
              {t('tableRowsCounter', {
                from:
                  1 +
                  table.getState().pagination.pageSize *
                    (table.getState().pagination.pageIndex ?? 0),
                to: Math.min(
                  table.getPrePaginationRowModel().rows.length,
                  table.getState().pagination.pageSize *
                    ((table.getState().pagination.pageIndex ?? 0) + 1),
                ),
                total: table.getPrePaginationRowModel().rows.length,
              })}
            </div>
          </div>

          <div className='eye:body-s eye:flex eye:items-center eye:justify-end eye:gap-8'>
            <Label
              className='eye:body-s eye:whitespace-nowrap'
              htmlFor='rowsPerPage'
            >
              {t('rowsPerPage')}
            </Label>
            <div className='eye:w-[93px]'>
              <Combobox<number>
                id='rowsPerPage'
                items={rowsPerPageOptions}
                value={table.getState().pagination.pageSize}
                onValueChange={(value) => {
                  if (value != null) table.setPageSize(value);
                }}
                filter={null}
                slotProps={{
                  searchInput: { className: 'eye:hidden' },
                  trigger: { 'aria-label': t('rowsPerPage') },
                }}
              />
            </div>
          </div>

          {table.getPageCount() > 1 && (
            <div className='eye:col-span-3 eye:flex eye:items-center eye:justify-center eye:gap-x-4xs'>
              {table.getPageCount() > 8 && (
                <Tooltip content={t('firstPage')}>
                  <Button
                    variant='ghost'
                    size='icon'
                    aria-label={t('firstPage')}
                    onClick={() => table.setPageIndex(0)}
                    disabled={!table.getCanPreviousPage()}
                  >
                    <ChevronFirst aria-hidden />
                  </Button>
                </Tooltip>
              )}
              <Tooltip content={t('previousPage')}>
                <Button
                  variant='ghost'
                  size='icon'
                  aria-label={t('previousPage')}
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                >
                  <ChevronLeftIcon aria-hidden />
                </Button>
              </Tooltip>

              {getPageRange(
                table.getState().pagination.pageIndex + 1,
                table.getPageCount(),
              ).map((page) => (
                <Button
                  key={page}
                  variant={
                    page === table.getState().pagination.pageIndex + 1
                      ? 'black'
                      : 'ghost'
                  }
                  size='icon'
                  aria-label={t('goToPage', { page })}
                  aria-current={
                    page === table.getState().pagination.pageIndex + 1
                      ? 'page'
                      : undefined
                  }
                  onClick={() => table.setPageIndex(page - 1)}
                >
                  {page}
                </Button>
              ))}

              <Tooltip content={t('nextPage')}>
                <Button
                  variant='ghost'
                  size='icon'
                  aria-label={t('nextPage')}
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  <ChevronRightIcon aria-hidden />
                </Button>
              </Tooltip>

              {table.getPageCount() > 8 && (
                <Tooltip content={t('lastPage')}>
                  <Button
                    variant='ghost'
                    size='icon'
                    aria-label={t('lastPage')}
                    onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                    disabled={!table.getCanNextPage()}
                  >
                    <ChevronLast aria-hidden />
                  </Button>
                </Tooltip>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
